import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Volume2, User, PieChart, TrendingUp, DollarSign, Activity, ArrowRight, BarChart3, Timer, Lock } from 'lucide-react';
import { useAuthContext } from '../context/AuthContext';
import { useAppContext } from '../context/AppContext';
import { getMarketStatus } from '../utils/session';

const DashboardPage = () => {
  const auth = useAuthContext();
  const app = useAppContext();
  const navigate = useNavigate();

  const [sessionState, setSessionState] = useState(getMarketStatus);
  useEffect(() => {
    const t = setInterval(() => setSessionState(getMarketStatus()), 60000);
    return () => clearInterval(t);
  }, []);

  const isLive = sessionState.status === 'live';

  const handleStreamClick = () => {
    if (!isLive) return;
    if (!auth.isSubscribed) {
      app.setShowTrialOfferModal(true);
      return;
    }
    navigate('/stream');
  };

  return (
    <div className="page">
      <div className="dashboard">
        {/* Welcome + Title */}
        <section className="dashboard__header">
          {auth.isLoggedIn && (
            <div className="dashboard__welcome">
              <User size={14} />
              Welcome back, {auth.user?.name || 'Trader'}
            </div>
          )}
          <h1 className="dashboard__title">Dashboard</h1>
        </section>

        {/* Stream CTA Card */}
        <section className="dashboard__cta-card">
          <div className="dashboard__cta-content">
            {sessionState.status === 'live' ? (
              <div className="dashboard__cta-badge">
                <span className="dashboard__cta-badge-dot" />
                Live Now
              </div>
            ) : (
              <div className="dashboard__cta-badge dashboard__cta-badge--offline">
                <Timer size={13} />
                {sessionState.label}
                {sessionState.countdown ? ` · opens in ${sessionState.countdown}` : ''}
              </div>
            )}
            <h2 className="dashboard__cta-title">Nasdaq Futures Trading Stream</h2>
            <p className="dashboard__cta-desc">
              {sessionState.status === 'live'
                ? 'Join the live session for real-time buy & sell signals on Nasdaq futures.'
                : sessionState.reason === 'weekend'
                  ? 'Markets are closed for the weekend. The next NY session opens Monday at 9:30 AM ET.'
                  : sessionState.reason === 'pre-market'
                    ? `Pre-market — the morning session opens at 9:30 AM ET${sessionState.countdown ? ` (in ${sessionState.countdown})` : ''}.`
                    : sessionState.reason === 'between'
                      ? `On break — the afternoon session opens at 1:00 PM ET${sessionState.countdown ? ` (in ${sessionState.countdown})` : ''}.`
                      : `Markets are closed for the day. Next session opens tomorrow at 9:30 AM ET${sessionState.countdown ? ` (in ${sessionState.countdown})` : ''}.`
              }
            </p>
            <button
              className="btn btn-primary"
              onClick={handleStreamClick}
              disabled={app.connecting || !isLive}
              title={!isLive ? 'The live stream is only available during NY session hours.' : undefined}
            >
              {app.connecting ? (
                <>
                  <span className="spinner" />
                  Connecting...
                </>
              ) : isLive ? (
                <>
                  <Volume2 size={18} />
                  Join Live Stream
                </>
              ) : (
                <>
                  <Lock size={18} />
                  {sessionState.countdown
                    ? `Opens in ${sessionState.countdown}`
                    : 'Stream offline'}
                </>
              )}
            </button>
          </div>
          <div className="dashboard__cta-visual">
            <Activity size={120} strokeWidth={0.8} />
          </div>
        </section>

        {/* Stats Grid */}
        <section className="dashboard__stats-grid">
          <div className="stat-card stat-card--accent">
            <div className="stat-card__icon stat-card__icon--green">
              <PieChart size={20} />
            </div>
            <div className="stat-card__value">73.4%</div>
            <div className="stat-card__label">Win Rate</div>
          </div>
          <div className="stat-card">
            <div className="stat-card__icon stat-card__icon--blue">
              <TrendingUp size={20} />
            </div>
            <div className="stat-card__value stat-card__value--green">$485</div>
            <div className="stat-card__label">Avg. Daily Profit</div>
          </div>
          <div className="stat-card">
            <div className="stat-card__icon stat-card__icon--default">
              <BarChart3 size={20} />
            </div>
            <div className="stat-card__value">1,352</div>
            <div className="stat-card__label">Total Trades</div>
          </div>
          <div className="stat-card">
            <div className="stat-card__icon stat-card__icon--green">
              <DollarSign size={20} />
            </div>
            <div className="stat-card__value stat-card__value--green">$47,320</div>
            <div className="stat-card__label">Total Profit</div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default DashboardPage;
